<!-- A time ("9:30") that opens a list of 15-minute slots, scrolled to the value (else to the slot nearest `near`, else
     9:00); each option can carry a duration ("1 h 30 min"). `none` adds a first option with no time ("No time"). Keyboard:
     ↑ ↓ Home End move, Enter picks, Esc closes. Used by WorkSlotField.vue (work start and end of work). -->
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { usePopover } from '../../composables/usePopover'
import { useI18n } from '../../composables/useI18n'
import { focusSlot, showTime } from '../../features/workSlot'

const props = defineProps<{ id: string; label: string; value: string; options: { time: string; dur: string }[]; placeholder: string
  disabled?: boolean; disabledTitle?: string; none?: boolean; near?: string }>()
const emit = defineEmits<{ pick: [hhmm: string] }>()
const { t } = useI18n()

const open = ref(false)
const root = ref<HTMLElement | null>(null), btn = ref<HTMLButtonElement | null>(null), list = ref<HTMLElement | null>(null)
usePopover(open, root, { esc: true })
watch(open, o => { if (!o) nextTick(() => { if (document.activeElement === document.body) btn.value?.focus() }) })
const times = computed(() => props.options.map(x => x.time))

function toggle(): void {
  if (open.value) { open.value = false; return }
  open.value = true
  nextTick(() => {
    const el = list.value
    if (!el) return
    const slot = focusSlot(times.value, props.value, props.near || '')
    const b = el.querySelector<HTMLButtonElement>(`[data-slot="${slot}"]`) || el.querySelector<HTMLButtonElement>('button[data-slot]')
    if (!b) return
    el.scrollTop = b.offsetTop - el.clientHeight / 2 + b.offsetHeight / 2
    b.focus({ preventScroll: true })
  })
}
function pick(v: string): void { emit('pick', v); open.value = false; btn.value?.focus() }
/** ↑ ↓ Home End between the options */
function listKey(e: KeyboardEvent): void {
  const items = [...(e.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('button[data-opt]')]
  const i = items.indexOf(document.activeElement as HTMLButtonElement)
  const j = e.key === 'ArrowDown' ? Math.min(items.length - 1, i + 1) : e.key === 'ArrowUp' ? Math.max(0, i - 1) : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : -1
  if (j < 0) return
  e.preventDefault()
  items[j]?.focus()
}
const optCls = (on: boolean) => ['w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-left text-xs tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand',
  on ? 'bg-brand text-white font-bold' : 'text-gray-200 hover:bg-brand/10']
</script>

<template>
  <div ref="root" class="relative flex-shrink-0">
    <button :id="props.id" ref="btn" type="button" :disabled="props.disabled" :aria-expanded="open" aria-haspopup="listbox"
      :title="props.disabled ? props.disabledTitle : props.label" :aria-label="`${props.label}: ${props.value ? showTime(props.value) : t('No time')}`"
      class="w-[5.25rem] h-12 px-2 bg-darkinput border border-gray-800 rounded-lg text-center tabular-nums font-bold leading-tight transition focus:outline-none focus:border-brand enabled:hover:border-brand/60 disabled:opacity-50 disabled:cursor-not-allowed" @click="toggle">
      <i class="fas fa-clock text-[11px] text-brand mr-1"></i><span :class="props.value ? 'text-gray-100' : 'text-gray-500 font-normal text-xs'">{{ props.value ? showTime(props.value) : props.placeholder }}</span>
    </button>
    <div v-if="open" class="absolute z-30 top-full mt-2 right-0 w-52 p-2 bg-darkcard border border-gray-800 rounded-2xl shadow-2xl">
      <div ref="list" role="listbox" :aria-label="props.label" class="max-h-60 overflow-y-auto space-y-0.5 pr-1" @keydown="listKey">
        <button v-if="props.none" type="button" data-opt role="option" :aria-selected="!props.value" :class="optCls(!props.value)" @click="pick('')">{{ t('No time') }}</button>
        <button v-for="s in props.options" :key="s.time" type="button" data-opt role="option" :data-slot="s.time" :aria-selected="s.time === props.value" :class="optCls(s.time === props.value)" @click="pick(s.time)">
          <span>{{ showTime(s.time) }}</span><span v-if="s.dur" :class="s.time === props.value ? 'text-white/80 font-normal' : 'text-gray-500'">{{ s.dur }}</span></button>
      </div>
    </div>
  </div>
</template>
